import moment from 'moment';
import { flow, get, map } from 'lodash';

import config from '../../config';
import {
  dayIsInArray,
  getScheduleWeekdaysFromSchedule,
  getScheduleWeekdaysOffFromSchedule,
  getWholeDaysOff,
  isWeekdayEnabled,
} from '../utils/schedule';
import { addDaysToDate, dayIsInRange, getStartOfDay } from '../utils/helpers';
import { ANY_BARBER } from '../constants';
import { getAllowMultipleServices as getShopAllowMultipleServices } from '../shops/selectors';
import { doServicesHaveAssignments, getBarberAssignments } from '../services/selectors';

const getBarberWholeDaysOff = (state) => {
  const daysOff = state.daysOff.get('items');
  return daysOff.size ? getWholeDaysOff(daysOff.toJS()) : [];
};

const getBarberDisabled = (barber, daysOff) => {
  const daysOn = barber.get('days_on');
  const advanceBookDays = barber.get('advanceBookDays');
  const from = getStartOfDay();
  const to = addDaysToDate(from, advanceBookDays);
  const hasDayOnInRange = daysOn && daysOn.some((item) => {
    const day = new Date(item.get('day'));
    return dayIsInRange(day, from, to);
  });
  if (hasDayOnInRange) return false;

  // Weekdays in Schedule start from 1 (Monday),
  // but in JS start from 0 (Sunday), hence rotating of weekdays array
  const scheduleWeekdays = getScheduleWeekdaysFromSchedule(barber.get('schedules').toJS());

  // Find a day in schedule that is not off day
  // Loop from start day until end day
  // Check if day in schedule is enabled and not off day
  let day = new Date(from);
  while (day <= to) {
    if (isWeekdayEnabled(day, scheduleWeekdays) && !dayIsInArray(day, daysOff)) {
      return false;
    }
    day = addDaysToDate(day, 1);
  }

  return true;
};

const filterByShopId = shopRoute => barber => barber.getIn(['shop', 'route']) === shopRoute;

export const getAllBarbersRaw = state => state.barbers.get('items').toJS();

export const getBarbers = (state, { shopRoute, serviceId, barberRoute }) => {
  const barbers = state.barbers.get('items')
    .filter(filterByShopId(shopRoute))
    .filter((barber) => {
      if (!serviceId) return true;
      const servicesIds = serviceId.split(';');
      let barberServices = barber.get('services') ? barber.get('services').toJS() : [];
      if (doServicesHaveAssignments(state)) {
        barberServices = getBarberAssignments(state, barber.get('id'));
      }

      return servicesIds.every(id => barberServices.some(service => id === service.id));
    });

  return barbers.map((barber) => {
    const nextBarberTime = barber.get('nextTime') || null;
    const timezone = barber.getIn(['shop', 'timezone']);
    const nextTime =
      moment(nextBarberTime).isValid() ? moment.tz(nextBarberTime, timezone) : null;
    const dateTimeFormat = 'MMMM D @ h:mmA';
    const nextTimeFormatted = nextTime
      ? nextTime.calendar(null, {
        sameDay: '[Today] @ h:mmA',
        nextWeek: dateTimeFormat,
        sameElse: dateTimeFormat,
      })
      : null;
    const thumbnail = barber.getIn(['photos', 0, 'thumbnail']);
    const isActive = barber.get('route') === barberRoute;
    const disabled = nextTime ? false : getBarberDisabled(barber, getBarberWholeDaysOff(state));

    return barber
      .set('nextTime', nextTime)
      .set('thumbnail', thumbnail)
      .set('nextTimeFormatted', nextTimeFormatted)
      .set('isActive', isActive)
      .set('disabled', disabled);
  }).toJS();
};

export const getBarbersCount =
  (state, { shopRoute }) => state.barbers.get('items').filter(filterByShopId(shopRoute)).size;

export const getBarberById =
  (state, id) => state.barbers.get('items').find(barber => barber.get('id') === id);

export const getBarberByRoute =
  (state, route) => state.barbers.get('items').find(barber => barber.get('route') === route);

export const getBarberByIdOrRoute =
  (state, idOrRoute) => getBarberById(state, idOrRoute) || getBarberByRoute(state, idOrRoute);

export const getTipEnabledForBarber = (state, route) => {
  const barber = getBarberByIdOrRoute(state, route);
  return barber && barber.get('tipEnabled');
};

export const getBarberBookNoPay = (state, route) => {
  const barber = getBarberByIdOrRoute(state, route);

  return barber && barber.get('bookNoPay');
};

export const getBarberShortName = (state, route) => {
  const barber = getBarberByIdOrRoute(state, route);

  if (!barber) return '';

  const lastName = barber.get('lastName') ? ` ${barber.get('lastName')[0]}.` : '';

  return `${barber.get('firstName')}${lastName}`;
};

export const getBarberAdvanceBookDays = (state, route) => {
  const barber = getBarberByIdOrRoute(state, route);
  return barber && barber.get('advanceBookDays');
};

export const getBarberId = (state, route) => {
  if (route === ANY_BARBER) return route;
  const barber = getBarberByIdOrRoute(state, route);
  return barber && barber.get('id');
};

export const getAllowMultipleServices = (state, route, shopRoute) => {
  if (route === ANY_BARBER) return getShopAllowMultipleServices(state, shopRoute);
  const barber = getBarberByIdOrRoute(state, route);
  return barber && barber.get('allowMultipleServices');
};

export const getHasBarberLoyaltyProgram = (state, route) => {
  const barber = getBarberByIdOrRoute(state, route);

  if (!barber) return false;

  const reward = barber.get('reward');
  return reward && reward.get('enabled');
};

export const getBarberName = (state, idOrRoute) => {
  const barber = getBarberByIdOrRoute(state, idOrRoute);
  return barber && `${barber.get('firstName')} ${barber.get('lastName')}`;
};

const getBarberSchedules = (state, idOrRoute) => flow(
  getBarberByIdOrRoute,
  barber => barber && barber.get('schedules'),
  schedules => schedules && schedules.toJS(),
)(state, idOrRoute);

const getBarberScheduleWeekdays = (state, idOrRoute) => flow(
  getBarberSchedules,
  getScheduleWeekdaysFromSchedule,
)(state, idOrRoute);

export const getBarberDayEnabled = (state, route, activeDay) => {
  const day = getStartOfDay(activeDay);
  const barber = getBarberByRoute(state, route);
  const scheduleWeekdays = getBarberScheduleWeekdays(state, route);
  return barber && isWeekdayEnabled(day, scheduleWeekdays) && !dayIsInArray(day, getBarberWholeDaysOff(state));
};

export const getBarberWeekdaysOff = (state, idOrRoute) => flow(
  getBarberSchedules,
  getScheduleWeekdaysOffFromSchedule,
)(state, idOrRoute);

const mapDaysStringToDate = days => map(days, item =>
  moment(item, config.apiDateFormat).toDate());

export const getBarberDaysOffAsDates = state => flow(
  getBarberWholeDaysOff,
  mapDaysStringToDate,
)(state);

const getBarberDaysOn = (barber) => {
  if (!barber) return null;
  const daysOn = barber.get('days_on');
  return daysOn.size ? daysOn.toJS() : [];
};

export const getBarberDaysOnAsDates = (state, idOrRoute) => flow(
  getBarberByIdOrRoute,
  getBarberDaysOn,
  items => map(items, item => get(item, 'day')),
  mapDaysStringToDate,
)(state, idOrRoute);

export const getBarberAdvanceCancelMins = flow(
  getBarberByIdOrRoute,
  barber => barber && barber.get('advanceCancelMins'),
);



// WEBPACK FOOTER //
// ./src/app/barbers/selectors.js